import React, { JSXElementConstructor, ReactElement, ReactNode, ReactPortal, useMemo, useState } from "react"
import { Box, Chip, FormControlLabel, FormLabel, Paper, Stack, Switch, Typography } from "@mui/material"
import { QRCodeCanvas } from "qrcode.react"

import { DataProvider, ListComponent } from "../GenericList"
// import MUIDataTable from "mui-datatables"
import { deleteTickets } from "./core/CreateEditOptions/deleteTicket"
import { PopupService } from "react-ui-components/components/PopupService"
import { ImageWidget } from "./CustomWidgets"
import CustomObjectFieldTabs from "./raffle/CustomObjectFieldTabs"
import {
  formatTicketPayload,
  getBatchName,
  getCodePrefix,
  getQrToken,
  getRaffleTicketsCount,
  getRawQrToken,
  getScanCooldownMinutes,
  getTicketPoints,
  getTicketTypes,
  getTicketValue,
  isUnlimitedTicketBatch,
  normalizeTicketBatchList,
  RaffleExpiryFields,
} from "./TicketsTableUtils"

const hiddenEditableColumnOptions = {
  display: false,
  download: false,
  filter: false,
  sort: false,
  viewColumns: false,
}

const formatTicketPropertyLabel = (key: string) =>
  key
    .replace(/Property$/i, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())

const getTicketProperties = (ticket: Record<string, any> | null | undefined) => {
  if (!ticket) {
    return []
  }

  return Object.entries(ticket)
    .filter(([key, value]) => {
      if (!/Property$/i.test(key)) {
        return false
      }

      if (value === null || value === undefined) {
        return false
      }

      return String(value).trim().length > 0
    })
    .map(([key, value]) => ({
      key,
      label: formatTicketPropertyLabel(key),
      value: String(value),
    }))
}

const renderLabelValue = (
  value:
    | string
    | number
    | boolean
    | ReactElement<any, string | JSXElementConstructor<any>>
    | Iterable<ReactNode>
    | ReactPortal
    | null
    | undefined,
) => (
  <div className="flex flex-wrap">
    <FormLabel>{value || value === 0 ? String(value) : "N/A"}</FormLabel>
  </div>
)

// Ticket batches expire independently of the parent raffle (their own `expirationDate`
// column), and the raffle itself can also end (drawn, or disabled by an admin) without that
// retroactively touching any batch's `expirationDate`. Both kinds of expired batches stay
// hidden by default (matches the trello card #207 complaint that they never disappeared) but
// can be revealed via the "Show expired" toggle below, since admins still need them for
// audit/support lookups.
const buildTicketsTableConfig = (includeExpired: boolean, raffle?: RaffleExpiryFields | null) => ({
  prepareFormData(ticket: Record<string, any>) {
    const value = getTicketValue(ticket)
    const points = getTicketPoints(ticket)

    return {
      ...ticket,
      ...(typeof value === "number" ? { value } : {}),
      ...(typeof points === "number" ? { points } : {}),
    }
  },
  getListLocation(id: any) {
    return ["raffles", id, "tickets"]
  },
  EDIT_TITLE: "Edit RaffleTickets",
  ADD_TITLE: "Add Raffle Ticket",
  LOADING_MESSAGE: "Loading Raffle Tickets...",
  TITLE: "Raffle Ticket Batches",
  IMAGES_BUCKET: "RaffleTicketsImages",
  prepareListData: (tickets: Record<string, any>[]) =>
    normalizeTicketBatchList(tickets, includeExpired, raffle),

  onRowClick:
    (list: { [x: string]: any }, _navigate: any) =>
      (_arr: any, index: { dataIndex: string | number }) => {
        const ticket = list[index.dataIndex]
        const qrToken = getQrToken(ticket)
        const rawQrToken = getRawQrToken(ticket)
        const codePrefix = getCodePrefix(ticket)
        const ticketTypes = getTicketTypes(ticket)
        const ticketProperties = getTicketProperties(ticket)
        const raffleTicketsCount = getRaffleTicketsCount(ticket)
        const scanCooldownMinutes = getScanCooldownMinutes(ticket)
        const unlimitedTotalScans = isUnlimitedTicketBatch(ticket)

        PopupService.setPopup(
          <Paper sx={{ p: 3, maxWidth: 560 }}>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography variant="h6">{getBatchName(ticket)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {[ticket?.raffleType, ticket?.code].filter(Boolean).join(" · ") || "Raffle ticket"}
                </Typography>
              </Stack>

              <Box
                sx={{
                  minHeight: 220,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                }}
              >
                {qrToken ? (
                  <QRCodeCanvas value={qrToken} size={180} level="M" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    QR token unavailable for this ticket.
                  </Typography>
                )}
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={
                    unlimitedTotalScans
                      ? "Available: Unlimited"
                      : `Available: ${Number(ticket?.availableCount || 0)}`
                  }
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={
                    ticket?.userScanCount > 0
                      ? `Scan limit: ${ticket.userScanCount}`
                      : "Unlimited scans"
                  }
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Cooldown: ${scanCooldownMinutes} min`}
                  size="small"
                  variant="outlined"
                />
                {codePrefix ? (
                  <Chip label={`Prefix: ${codePrefix}`} size="small" variant="outlined" />
                ) : null}
                {ticket?.sundryShortUrl ? (
                  <Chip label="Sundry QR" size="small" color="primary" variant="outlined" />
                ) : null}
                <Chip
                  label={`Points: ${getTicketPoints(ticket) ?? 0}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Value: ${getTicketValue(ticket) ?? 0}`}
                  size="small"
                  variant="outlined"
                />
                {raffleTicketsCount !== null ? (
                  <Chip
                    label={`Tickets per scan: ${raffleTicketsCount}`}
                    size="small"
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              {ticketTypes.length ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Ticket Types</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {ticketTypes.map((type) => (
                      <Chip key={type} label={type} size="small" />
                    ))}
                  </Stack>
                </Stack>
              ) : null}

              {ticketProperties.length ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Ticket Properties</Typography>
                  {ticketProperties.map((property) => (
                    <Stack
                      key={property.key}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {property.label}
                      </Typography>
                      <Typography variant="body2">{property.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : null}

              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {ticket?.sundryShortUrl ? "Sundry QR short URL" : "Generated scan code"}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {qrToken || "This ticket does not have a stored QR token."}
                </Typography>
                {ticket?.sundryQrId ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Sundry QR ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {ticket.sundryQrId}
                    </Typography>
                  </>
                ) : null}
                {rawQrToken && rawQrToken !== qrToken ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Raw compact token
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {rawQrToken}
                    </Typography>
                  </>
                ) : null}
              </Stack>
            </Stack>
          </Paper>,
        )
      },

  columnsProperties: [
    { hidden: true, name: "id", label: "Id" },
    { name: "disabled", default: false, label: "Disabled", type: "boolean" },
    { hidden: true, name: "raffleId", label: "Raffle ID" },
    {
      hidden: true,
      name: "qrCodeDataUrl",
      label: "QR Code",
      options: {
        customBodyRender: renderLabelValue,
      },
    },
    {
      required: false,
      name: "name",
      label: "Batch Name",
      options: {
        customBodyRender: (value: unknown) => {
          const label = typeof value === "string" && value.trim() ? value.trim() : "Not named"

          return (
            <Stack spacing={0.5} sx={{ minWidth: 220 }}>
              <Typography variant="body2" fontWeight={600}>
                {label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Searchable label for the generated batch
              </Typography>
            </Stack>
          )
        },
      },
    },
    {
      required: false,
      name: "availableCount",
      label: "Available Count",
      type: "number",
      options: {
        customBodyRender: (value: unknown) =>
          renderLabelValue(value === -1 || value === "unlimited" ? "Unlimited" : value as any),
      },
    },
    {
      required: false,
      name: "unlimitedTotalScans",
      label: "Unlimited Total QR Scans",
      type: "boolean",
      options: hiddenEditableColumnOptions,
    },
    {
      name: "raffleType",
      label: "Raffle Type",
      options: {
        customBodyRender: (
          value:
            | string
            | number
            | boolean
            | ReactElement<any, string | JSXElementConstructor<any>>
            | Iterable<ReactNode>
            | ReactPortal
            | null
            | undefined,
        ) => {
          if (!value || typeof value !== "string") {
            return <FormLabel>N/A</FormLabel>
          }

          return <div dangerouslySetInnerHTML={{ __html: value }} />
        },
      },
    },
    {
      name: "code",
      label: "Ticket Code",
    },
    {
      required: false,
      name: "ticketsPointsValue",
      label: "Ticket Value",
      type: "number",
      // options: {
      //   customBodyRender: (_: any, tableMeta: any) => {
      //     const ticket = tableMeta.rowData && tableMeta.rowData.length ? tableMeta.rowData[tableMeta.columnIndex] : null;
      //     // fallback: try to get the full ticket object if available
      //     return <FormLabel>{getTicketValue(ticket)}</FormLabel>;
      //   },
      // },
    },
    {
      required: false,
      type: "number",
      name: "ticketsPoints",
      label: "Points",
      // options: {
      //   customBodyRender: (_: any, tableMeta: any) => {
      //     const ticket = tableMeta.rowData && tableMeta.rowData.length ? tableMeta.rowData[tableMeta.columnIndex] : null;
      //     return <FormLabel>{getTicketPoints(ticket)}</FormLabel>;
      //   },
      // },
    },
    {
      required: false,
      type: "number",
      name: "userScanCount",
      label: "User Scan Limit",
    },
    {
      required: false,
      type: "number",
      name: "scanCooldownMinutes",
      label: "Scan Cooldown (Min)",
    },
    {
      name: "expirationDate",
      label: "Expiration Date",
      uiType: "datetime2",
    },
    {
      name: "createdDate",
      label: "Created Date",
      uiType: "datetime2",
      type: "number",
      hideEdit: true,
    },
    {
      required: false,
      type: "number",
      name: "raffleTicketsCount",
      label: "Tickets Granted Per Scan",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "codePrefix",
      label: "Code Prefix",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "qrCodeData",
      label: "Generated Scan Code",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "rawQrCodeData",
      label: "Raw Compact Ticket Token",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "sundryQrId",
      label: "Sundry QR ID",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "sundryShortCode",
      label: "Sundry Short Code",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "sundryShortUrl",
      label: "Sundry Short URL",
      options: hiddenEditableColumnOptions,
    },
    {
      required: false,
      name: "sundryQrStatus",
      label: "Sundry QR Status",
      options: hiddenEditableColumnOptions,
    },
    { name: "isSold", label: "Is Sold", type: "boolean" },
    { name: "isWinner", label: "Is Winner", type: "boolean" },
    { name: "createdBy", label: "Created By" },
    { name: "order", label: "Order", type: "number" },
    { name: "index", label: "Index", type: "number", required: false },
  ],

  options: {
    fixedHeader: true,
    selectableRows: "none",
    responsive: "standard",
    rowsPerPage: 25,
    rowsPerPageOptions: [10, 25, 50, 100],
    searchOpen: true,
    searchPlaceholder: "Search batch name, scan code, or raffle type",
    jumpToPage: true,
    download: false,
    print: false,
    sortOrder: {
      name: "createdDate",
      direction: "desc",
    },
    setTableProps: () => ({
      stickyHeader: true,
      sx: {
        "& .MuiTableCell-head": {
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "#f5f5f5",
        },
      },
    }),
    setRowProps: (_row: any, dataIndex: number) => ({
      style: {
        backgroundColor: dataIndex % 2 === 0 ? "#f7f7f7" : "#ffffff",
      },
    }),
  },

  uiSchema: {
    qrCodeDataUrl: {
      "ui:widget": "image",
    },
    code: {
      "ui:disabled": true,
    },
    qrCodeData: {
      "ui:disabled": true,
      "ui:widget": "textarea",
    },
    rawQrCodeData: {
      "ui:disabled": true,
      "ui:widget": "textarea",
    },
    codePrefix: {
      "ui:help": "Prepended in front of the raw compact token to build the final scan code.",
    },
    createdDate: {
      "ui:widget": "datetime",
      "ui:disabled": true,
    },
    createdBy: {
      "ui:disabled": true,
    },
    index: {
      "ui:disabled": true,
    },
    expirationDate: {
      "ui:widget": "datetime",
    },
    description: {
      "ui:widget": "textarea",
    },
  },

  widgets: { image: ImageWidget },
  ObjectFieldTemplate: CustomObjectFieldTabs,
})

const TicketsTable = ({ raffleId, raffle }: { raffleId: any; raffle?: RaffleExpiryFields | null }) => {
  const [showExpired, setShowExpired] = useState(false)
  const data = useMemo(() => buildTicketsTableConfig(showExpired, raffle), [showExpired, raffle])

  const TicketsList = () => {
    return <ListComponent />
  }

  // const [tableData, setTableData] = React.useState<any[]>([])
  // const [loading, setLoading] = React.useState(true)
  // const path = `raffles/${raffleId}/tickets`

  // // Fetch tickets from Firestore (reuse getData pattern if available)
  // React.useEffect(() => {
  //   async function fetchTickets() {
  //     setLoading(true)
  //     try {
  //       // Use getData pattern if available, else fallback to direct fetch
  //       const res = await import("./core/CreateEditOptions/getData")
  //       const { getData } = res
  //       await getData({
  //         setData: setTableData,
  //         path,
  //         getMap: (item: any) => item,
  //         setLoading,
  //         isCollection: true,
  //       })
  //     } catch (err) {
  //       setTableData([])
  //       setLoading(false)
  //     }
  //   }
  //   fetchTickets()
  // }, [raffleId])

  // const columns = data.columnsProperties?.filter((col: any) => !col.hidden) || []

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showExpired}
              onChange={(event) => setShowExpired(event.target.checked)}
              size="small"
            />
          }
          label="Show expired ticket batches"
        />
      </Box>
      <DataProvider {...data} mainId={raffleId} format={formatTicketPayload}>
        <TicketsList />
      </DataProvider>
    </div>
  )
  // return (
  //   <MUIDataTable
  //     title={data.TITLE}
  //     data={tableData}
  //     columns={columns}
  //     options={{
  //       selectableRows: "multiple",
  //       onRowsDelete: async (rowsDeleted: any) => {
  //         const idsToDelete = rowsDeleted.data.map((row: any) => tableData[row.dataIndex]?.id).filter(Boolean)
  //         if (!idsToDelete.length) return
  //         const confirmed = window.confirm(`Delete ${idsToDelete.length} ticket(s)? This cannot be undone.`)
  //         if (!confirmed) return
  //         try {
  //           await deleteTickets({ path, ids: idsToDelete })
  //           // Refresh table
  //           const res = await import("./core/CreateEditOptions/getData")
  //           const { getData } = res
  //           await getData({
  //             setData: setTableData,
  //             path,
  //             getMap: (item: any) => item,
  //             setLoading,
  //           })
  //         } catch (err) {
  //           alert("Failed to delete tickets: " + (err?.message || err))
  //         }
  //       },
  //       ...data.options,
  //       loading,
  //     }}
  //   />
  // )
}

export default TicketsTable
